import * as THREE from "three"
import BaseThreeScene from "../../scenes/BaseThreeScene"
import { createGraphEdge, createGraphNode } from "../../scenes/graphPrimitives"
import { createTextSprite, updateTextSprite } from "../../scenes/shared/textSpritePrimitives"
import { buildTreeById, createBinaryTreeLayout } from "../../scenes/shared/treeUtils"

const DEFAULT_TREE_INPUT = {
    rootId: 3,
    targetAId: 7,
    targetBId: 4,
    nodes: [
        { id: 3, leftId: 5, rightId: 1 },
        { id: 5, leftId: 6, rightId: 2 },
        { id: 1, leftId: 0, rightId: 8 },
        { id: 6, leftId: null, rightId: null },
        { id: 2, leftId: 7, rightId: 4 },
        { id: 0, leftId: null, rightId: null },
        { id: 8, leftId: null, rightId: null },
        { id: 7, leftId: null, rightId: null },
        { id: 4, leftId: null, rightId: null },
    ],
};

const GRAPH_SCALE = 1.02;
const GRAPH_OFFSET = new THREE.Vector3(1.15, 0, 0);

const BACKDROP_Z = -2.25;
const LANE_Z = -0.95;
const LANE_HEIGHT = 0.62;

const NODE_IDLE_COLOR = new THREE.Color(0x334155);
const NODE_ACTIVE_COLOR = new THREE.Color(0xe0f2fe);
const NODE_TARGET_COLOR = new THREE.Color(0xf59e0b);
const NODE_FOUND_TARGET_COLOR = new THREE.Color(0xfbbf24);
const NODE_RESOLVED_NONE_COLOR = new THREE.Color(0x1e293b);
const NODE_RESOLVED_ONE_COLOR = new THREE.Color(0x14b8a6);
const NODE_RETURN_TWO_COLOR = new THREE.Color(0x7c3aed);
const NODE_LCA_COLOR = new THREE.Color(0xd946ef);

const EDGE_IDLE_COLOR = new THREE.Color(0x334155);
const EDGE_ACTIVE_COLOR = new THREE.Color(0xfbbf24);
const EDGE_RESOLVED_COLOR = new THREE.Color(0x22c55e);

function edgeKey(fromId, toId) {
    return `${fromId}:${toId}`;
}

function normalizeTreeInput(input) {
    const source = input || DEFAULT_TREE_INPUT;

    return {
        rootId: source.rootId,
        targetAId: source.targetAId,
        targetBId: source.targetBId,
        nodes: source.nodes.map((node) => ({
            id: node.id,
            leftId: node.leftId,
            rightId: node.rightId,
        })),
    };
}

function buildLcaSteps(treeInput) {
    const { rootId, targetAId, targetBId, nodes } = treeInput;
    const treeById = buildTreeById(nodes);

    const steps = [
        {
            type: "mark-targets",
            targetAId,
            targetBId,
        },
    ];

    let lcaId = null;

    function dfs(nodeId, parentId = null) {
        if (nodeId === null) {
            return 0;
        }

        const node = treeById.get(nodeId);
        steps.push({
            type: "enter-node",
            nodeId,
            parentId,
        });

        const selfCount =
            (nodeId === targetAId ? 1 : 0) +
            (nodeId === targetBId ? 1 : 0);

        if (nodeId === targetAId) {
            steps.push({
                type: "match-target",
                nodeId,
                targetLabel: "P",
            });
        }

        if (nodeId === targetBId) {
            steps.push({
                type: "match-target",
                nodeId,
                targetLabel: "Q",
            });
        }

        const leftCount =
            node.leftId !== null ? dfs(node.leftId, nodeId) : 0;
        const rightCount =
            node.rightId !== null ? dfs(node.rightId, nodeId) : 0;

        const returnCount = Math.min(2, selfCount + leftCount + rightCount);
        const isLca = lcaId === null && returnCount === 2;

        if (isLca) {
            lcaId = nodeId;
        }

        steps.push({
            type: "resolve-node",
            nodeId,
            parentId,
            leftCount,
            rightCount,
            selfCount,
            returnCount,
            isLca,
        });

        return returnCount;
    }

    dfs(rootId, null);

    steps.push({
        type: "complete",
        lcaId,
    });

    return steps;
}

function createStepLogEntry(step, stepIndex) {
    const stepNumber = stepIndex + 1

    switch (step.type) {
        case "mark-targets":
            return {
                id: `lca-step-${stepNumber}-targets`,
                stepIndex: stepNumber,
                kind: "Setup",
                title: "Mark target nodes",
                message: `The search will locate the lowest common ancestor of nodes ${step.targetAId} and ${step.targetBId}.`,
            }

        case "enter-node":
            return {
                id: `lca-step-${stepNumber}-enter-${step.nodeId}`,
                stepIndex: stepNumber,
                kind: "Enter",
                title: `Enter node ${step.nodeId}`,
                message:
                    step.parentId === null
                        ? `The DFS begins at the root node ${step.nodeId}.`
                        : `The DFS descends from node ${step.parentId} into node ${step.nodeId}.`,
            }

        case "match-target":
            return {
                id: `lca-step-${stepNumber}-match-${step.nodeId}`,
                stepIndex: stepNumber,
                kind: "Match",
                title: `Match target ${step.targetLabel} at node ${step.nodeId}`,
                message: `Node ${step.nodeId} matches target ${step.targetLabel}, so this subtree now contributes one target back up the recursion.`,
            }

        case "resolve-node":
            return {
                id: `lca-step-${stepNumber}-resolve-${step.nodeId}`,
                stepIndex: stepNumber,
                kind: "Resolve",
                title: step.isLca
                    ? `Resolve node ${step.nodeId} as LCA`
                    : `Resolve node ${step.nodeId}`,
                message: step.isLca
                    ? `Node ${step.nodeId} is the first postorder node whose subtree contains both targets, so it becomes the lowest common ancestor.`
                    : `Node ${step.nodeId} resolves with left=${step.leftCount}, right=${step.rightCount}, self=${step.selfCount}, returning ${step.returnCount} target matches to its parent.`,
            }

        case "complete":
            return {
                id: `lca-step-${stepNumber}-complete`,
                stepIndex: stepNumber,
                kind: "Result",
                title: "Traversal complete",
                message: `The DFS is complete. The lowest common ancestor is node ${step.lcaId}.`,
            }

        default:
            return {
                id: `lca-step-${stepNumber}-unknown`,
                stepIndex: stepNumber,
                kind: "Step",
                title: "Advance step",
                message: "Advances the lowest common ancestor visualization to the next state.",
            }
    }
}

export default class LowestCommonAncestorScene extends BaseThreeScene {
    setup(props) {
        this.treeInput = normalizeTreeInput(props.input);
        this.rootId = this.treeInput.rootId;
        this.targetAId = this.treeInput.targetAId;
        this.targetBId = this.treeInput.targetBId;
        this.treeLayout = createBinaryTreeLayout(this.treeInput);
        this.laneYValues = Array.from(
            new Set(this.treeLayout.map((node) => node.position.y * GRAPH_SCALE))
        ).sort((a, b) => b - a);

        const maxAbsTreeX = Math.max(
            ...this.treeLayout.map((node) => Math.abs(node.position.x * GRAPH_SCALE)),
            1
        )
        const maxAbsTreeY = Math.max(
            ...this.treeLayout.map((node) => Math.abs(node.position.y * GRAPH_SCALE)),
            1
        )

        this.scene.background = new THREE.Color(0x050816)
        this.scene.fog = new THREE.Fog(0x050816, 11, 22)
        this.camera.position.set(0.15, 0, 7.5 + maxAbsTreeX * 0.58 + maxAbsTreeY * 0.6)

        const ambientLight = new THREE.AmbientLight(0xffffff, 1.05)
        this.scene.add(ambientLight)

        const keyLight = new THREE.DirectionalLight(0xffffff, 1.7)
        keyLight.position.set(4, 8, 10)
        this.scene.add(keyLight)

        const leftLight = new THREE.PointLight(0x60a5fa, 16, 18, 2)
        leftLight.position.set(-5.5, 2.8, 5)
        this.scene.add(leftLight)

        const rightLight = new THREE.PointLight(0xd946ef, 15, 18, 2)
        rightLight.position.set(5.5, -1.5, 5)
        this.scene.add(rightLight)

        this.graphRoot = new THREE.Group()
        this.scene.add(this.graphRoot)

        this.treeNodes = new Map()
        this.treePositions = new Map()
        this.nodeLabels = new Map()
        this.returnBadges = new Map()
        this.targetBadges = new Map()
        this.treeEdges = new Map()

        this.backdrop = null
        this.laneMeshes = []
        this.presentationTime = 0
        this.lastBackdropAspect = null

        this.buildBackdrop()
        this.buildTree()
        this.buildIndicators()
        this.resetAnimationState()
        this.advanceStep()
    }

    getViewportSizeAtZ(worldZ) {
        const distance = Math.abs(this.camera.position.z - worldZ)
        const verticalFovRadians = THREE.MathUtils.degToRad(this.camera.fov)
        const height = 2 * Math.tan(verticalFovRadians * 0.5) * distance
        const width = height * this.camera.aspect

        return { width, height }
    }

    buildBackdrop() {
        this.backdrop = new THREE.Mesh(
            new THREE.PlaneGeometry(1, 1),
            new THREE.MeshBasicMaterial({
                color: 0x071226,
                transparent: true,
                opacity: 0.76,
            })
        )
        this.backdrop.position.set(this.camera.position.x, 0, BACKDROP_Z)
        this.backdrop.renderOrder = -20
        this.scene.add(this.backdrop)

        const laneMaterial = new THREE.MeshBasicMaterial({
            color: 0x0f172a,
            transparent: true,
            opacity: 0.94,
        })

        this.laneMeshes = this.laneYValues.map((y) => {
            const lane = new THREE.Mesh(
                new THREE.PlaneGeometry(1, 1),
                laneMaterial.clone()
            )
            lane.position.set(this.camera.position.x, y, LANE_Z)
            lane.renderOrder = -10
            lane.userData.baseY = y
            this.scene.add(lane)
            return lane
        })

        this.updateBackdropLayout()
    }

    updateBackdropLayout() {
        if (!this.backdrop) {
            return
        }

        const backdropSize = this.getViewportSizeAtZ(BACKDROP_Z)
        this.backdrop.position.x = this.camera.position.x
        this.backdrop.scale.set(
            backdropSize.width * 1.06,
            backdropSize.height * 1.06,
            1
        )

        const laneSize = this.getViewportSizeAtZ(LANE_Z)
        const laneWidth = laneSize.width * 1.08

        this.laneMeshes.forEach((lane) => {
            lane.position.x = this.camera.position.x
            lane.scale.set(laneWidth, LANE_HEIGHT, 1)
        })

        this.lastBackdropAspect = this.camera.aspect
    }

    onResize() {
        this.updateBackdropLayout()
    }

    buildTree() {
        this.treeLayout.forEach((nodeData) => {
            const position = nodeData.position
                .clone()
                .multiplyScalar(GRAPH_SCALE)
                .add(GRAPH_OFFSET)

            this.treePositions.set(nodeData.id, position)

            const node = createGraphNode({
                id: nodeData.id,
                position,
                radius: 0.34,
                color: NODE_IDLE_COLOR.getHex(),
            })

            node.userData.baseY = position.y
            this.treeNodes.set(nodeData.id, node)
            this.graphRoot.add(node)

            const label = createTextSprite({
                text: nodeData.id,
                width: 128,
                height: 56,
                fontSize: 24,
                textColor: "#ffffff",
                fillStyle: "rgba(2, 6, 23, 0)",
                strokeStyle: "rgba(255, 255, 255, 0)",
                outlineColor: "rgba(2, 6, 23, 0.92)",
                outlineWidth: 8,
                shadowColor: "rgba(0, 0, 0, 0.35)",
                shadowBlur: 5,
                scale: [0.8, 0.34],
            })
            label.position.copy(position.clone().add(new THREE.Vector3(0, 0, 0.44)))
            label.renderOrder = 25
            this.nodeLabels.set(nodeData.id, label)
            this.graphRoot.add(label)

            const returnBadge = createTextSprite({
                text: "-",
                width: 112,
                height: 66,
                fontSize: 28,
                textColor: "#dbeafe",
                fillStyle: "rgba(24, 24, 27, 0.96)",
                strokeStyle: "rgba(255, 255, 255, 0.14)",
                outlineColor: "rgba(0, 0, 0, 0.96)",
                outlineWidth: 8,
                shadowColor: "rgba(0, 0, 0, 0.42)",
                shadowBlur: 6,
                scale: [0.62, 0.38],
            })
            returnBadge.position.copy(
                position.clone().add(new THREE.Vector3(0, 0.82, 0.08))
            )
            returnBadge.renderOrder = 26
            this.returnBadges.set(nodeData.id, returnBadge)
            this.graphRoot.add(returnBadge)

            if (nodeData.id === this.targetAId || nodeData.id === this.targetBId) {
                const targetBadge = createTextSprite({
                    text: nodeData.id === this.targetAId ? "P" : "Q",
                    width: 92,
                    height: 58,
                    fontSize: 24,
                    textColor: "#ffffff",
                    fillStyle:
                        nodeData.id === this.targetAId
                            ? "rgba(180, 83, 9, 0.98)"
                            : "rgba(147, 51, 234, 0.98)",
                    strokeStyle: "rgba(255, 255, 255, 0.16)",
                    outlineColor: "rgba(0, 0, 0, 0.9)",
                    outlineWidth: 6,
                    shadowColor: "rgba(0, 0, 0, 0.35)",
                    shadowBlur: 5,
                    scale: [0.48, 0.32],
                })
                targetBadge.position.copy(
                    position.clone().add(
                        new THREE.Vector3(
                            nodeData.id === this.targetAId ? -0.34 : 0.34,
                            0.3,
                            0.44
                        )
                    )
                )
                targetBadge.renderOrder = 27
                this.targetBadges.set(nodeData.id, targetBadge)
                this.graphRoot.add(targetBadge)
            }
        })

        this.treeLayout.forEach((nodeData) => {
            const fromPosition = this.treePositions.get(nodeData.id)

            if (nodeData.leftId !== null) {
                const edge = createGraphEdge({
                    fromId: nodeData.id,
                    toId: nodeData.leftId,
                    start: fromPosition,
                    end: this.treePositions.get(nodeData.leftId),
                    color: EDGE_IDLE_COLOR.getHex(),
                })
                this.treeEdges.set(edgeKey(nodeData.id, nodeData.leftId), edge)
                this.graphRoot.add(edge)
            }

            if (nodeData.rightId !== null) {
                const edge = createGraphEdge({
                    fromId: nodeData.id,
                    toId: nodeData.rightId,
                    start: fromPosition,
                    end: this.treePositions.get(nodeData.rightId),
                    color: EDGE_IDLE_COLOR.getHex(),
                })
                this.treeEdges.set(edgeKey(nodeData.id, nodeData.rightId), edge)
                this.graphRoot.add(edge)
            }
        })
    }

    buildIndicators() {
        this.activeRing = new THREE.Mesh(
            new THREE.RingGeometry(0.42, 0.5, 48),
            new THREE.MeshBasicMaterial({
                color: 0xffffff,
                transparent: true,
                opacity: 0.92,
                side: THREE.DoubleSide,
            })
        )
        this.graphRoot.add(this.activeRing)
    }

    resetAnimationState() {
        this.steps = buildLcaSteps(this.treeInput)
        this.stepIndex = -1
        this.currentStep = null
        this.currentStepElapsed = 0

        this.activeNodeId = null
        this.activeEdgeKey = null
        this.callStack = []
        this.foundTargetIds = new Set()
        this.resolvedNodeIds = new Set()
        this.returnCounts = new Map()
        this.candidateLcaId = null
        this.finalLcaId = null

        this.presentationTime = 0
        this.resetStepLog([])

        this.replaceUiState({
            targetAId: this.targetAId,
            targetBId: this.targetBId,
            activeNodeId: null,
            foundTargetIds: [],
            candidateLcaId: null,
            stack: [],
            returnEntries: this.treeLayout.map((node) => ({
                nodeId: node.id,
                value: "-",
            })),
        })

        this.syncPlaybackState()
    }

    syncUiState() {
        this.replaceUiState({
            targetAId: this.targetAId,
            targetBId: this.targetBId,
            activeNodeId: this.activeNodeId,
            foundTargetIds: Array.from(this.foundTargetIds),
            candidateLcaId: this.candidateLcaId,
            stack: this.callStack.slice(),
            returnEntries: this.treeLayout.map((node) => ({
                nodeId: node.id,
                value: this.returnCounts.has(node.id)
                    ? this.returnCounts.get(node.id)
                    : "-",
            })),
        })
    }

    getStepDuration(step) {
        switch (step.type) {
            case "mark-targets":
                return 1.2
            case "enter-node":
                return 0.75
            case "match-target":
                return 0.65
            case "resolve-node":
                return step.isLca ? 1.2 : 0.85
            case "complete":
                return 2.0
            default:
                return 0.8
        }
    }

    syncPlaybackState() {
        this.setPlaybackState({
            stepIndex: Math.max(0, this.stepIndex + 1),
            stepCount: this.steps.length,
            hasNextStep: this.stepIndex < this.steps.length - 1,
            isComplete:
                this.currentStep !== null && this.currentStep.type === "complete",
        })
    }

    advanceStep() {
        const nextStepIndex = this.stepIndex + 1

        if (nextStepIndex >= this.steps.length) {
            this.syncPlaybackState()
            this.syncUiState()
            return false
        }

        this.stepIndex = nextStepIndex
        this.currentStepElapsed = 0
        this.currentStep = this.steps[this.stepIndex] || null
        this.applyStep(this.currentStep)
        this.appendStepLog(createStepLogEntry(this.currentStep, this.stepIndex))
        this.syncPlaybackState()
        this.syncUiState()

        return true
    }

    applyStep(step) {
        if (!step) {
            return
        }

        switch (step.type) {
            case "mark-targets": {
                this.activeNodeId = null
                this.activeEdgeKey = null
                break
            }

            case "enter-node": {
                this.activeNodeId = step.nodeId
                this.activeEdgeKey =
                    step.parentId !== null
                        ? edgeKey(step.parentId, step.nodeId)
                        : null

                if (!this.callStack.includes(step.nodeId)) {
                    this.callStack.push(step.nodeId)
                }
                break
            }

            case "match-target": {
                this.activeNodeId = step.nodeId
                this.foundTargetIds.add(step.nodeId)
                break
            }

            case "resolve-node": {
                this.activeNodeId = step.nodeId
                this.activeEdgeKey = null
                this.returnCounts.set(step.nodeId, step.returnCount)
                this.resolvedNodeIds.add(step.nodeId)

                if (step.isLca) {
                    this.candidateLcaId = step.nodeId
                }

                if (
                    this.callStack.length > 0 &&
                    this.callStack[this.callStack.length - 1] === step.nodeId
                ) {
                    this.callStack.pop()
                } else {
                    this.callStack = this.callStack.filter(
                        (nodeId) => nodeId !== step.nodeId
                    )
                }
                break
            }

            case "complete": {
                this.activeNodeId = null
                this.activeEdgeKey = null
                this.callStack = []
                this.finalLcaId = step.lcaId
                this.candidateLcaId = step.lcaId
                break
            }

            default:
                break
        }
    }

    onResetPlayback() {
        this.resetAnimationState()
        this.advanceStep()
    }

    onStepForward() {
        this.advanceStep()
    }

    update(delta) {
        if (!this.graphRoot || !this.currentStep) {
            return
        }

        if (this.camera.aspect !== this.lastBackdropAspect) {
            this.updateBackdropLayout()
        }

        const playbackDelta = this.isPlaying ? delta : 0
        this.presentationTime += playbackDelta
        this.currentStepElapsed += playbackDelta

        if (
            this.isPlaying &&
            this.currentStepElapsed >= this.getStepDuration(this.currentStep)
        ) {
            const advanced = this.advanceStep()
            if (!advanced) {
                this.pause()
            }
        }

        this.updateGraphMotion(this.presentationTime)
        this.updateNodes(this.presentationTime)
        this.updateEdges()
        this.updateLabels()
        this.updateReturnBadges(this.presentationTime)
        this.updateTargetBadges()
        this.updateActiveRing(this.presentationTime)
    }

    updateGraphMotion(elapsed) {
        this.graphRoot.rotation.y = Math.sin(elapsed * 0.18) * 0.05
        this.graphRoot.rotation.x = Math.cos(elapsed * 0.16) * 0.018
        this.graphRoot.position.y = Math.sin(elapsed * 0.55) * 0.05
    }

    updateNodes(elapsed) {
        this.treeNodes.forEach((node, nodeId) => {
            const isActive = nodeId === this.activeNodeId
            const isTarget = nodeId === this.targetAId || nodeId === this.targetBId
            const isFoundTarget = this.foundTargetIds.has(nodeId)
            const isCandidateLca = nodeId === this.candidateLcaId
            const isResolved = this.resolvedNodeIds.has(nodeId)
            const returnCount = this.returnCounts.has(nodeId)
                ? this.returnCounts.get(nodeId)
                : null

            let targetColor = NODE_IDLE_COLOR
            let targetScale = 1
            let targetEmissiveIntensity = 0.18

            if (isResolved && returnCount === 0) {
                targetColor = NODE_RESOLVED_NONE_COLOR
                targetEmissiveIntensity = 0.14
            }

            if (isResolved && returnCount === 1) {
                targetColor = NODE_RESOLVED_ONE_COLOR
                targetEmissiveIntensity = 0.38
            }

            if (isResolved && returnCount === 2) {
                targetColor = NODE_RETURN_TWO_COLOR
                targetEmissiveIntensity = 0.45
            }

            if (isTarget) {
                targetColor = NODE_TARGET_COLOR
                targetScale = 1.04
                targetEmissiveIntensity = 0.32
            }

            if (isFoundTarget) {
                targetColor = NODE_FOUND_TARGET_COLOR
                targetScale = 1.08
                targetEmissiveIntensity = 0.48
            }

            if (isCandidateLca) {
                targetColor = NODE_LCA_COLOR
                targetScale = 1.16
                targetEmissiveIntensity = 0.72
            }

            if (isActive) {
                targetColor = NODE_ACTIVE_COLOR
                targetScale = 1.18 + Math.sin(elapsed * 4.5) * 0.03
                targetEmissiveIntensity = 0.9
            }

            const bob = Math.sin(elapsed * 1.6 + nodeId * 0.45) * 0.03
            node.position.y = THREE.MathUtils.lerp(
                node.position.y,
                node.userData.baseY + bob,
                0.12
            )

            const nextScale = THREE.MathUtils.lerp(node.scale.x, targetScale, 0.14)
            node.scale.setScalar(nextScale)
            node.material.color.lerp(targetColor, 0.14)
            node.material.emissive.lerp(targetColor, 0.14)
            node.material.emissiveIntensity = THREE.MathUtils.lerp(
                node.material.emissiveIntensity,
                targetEmissiveIntensity,
                0.14
            )
        })
    }

    updateEdges() {
        this.treeEdges.forEach((edge, nextEdgeKey) => {
            const childResolved = this.resolvedNodeIds.has(edge.userData.toId)
            const isActive = nextEdgeKey === this.activeEdgeKey

            let targetColor = EDGE_IDLE_COLOR
            let targetOpacity = 0.56

            if (childResolved) {
                targetColor = EDGE_RESOLVED_COLOR
                targetOpacity = 0.86
            }

            if (isActive) {
                targetColor = EDGE_ACTIVE_COLOR
                targetOpacity = 0.98
            }

            edge.material.color.lerp(targetColor, 0.18)
            edge.material.opacity = THREE.MathUtils.lerp(
                edge.material.opacity,
                targetOpacity,
                0.18
            )
        })
    }

    updateLabels() {
        this.nodeLabels.forEach((label, nodeId) => {
            const isActive = this.activeNodeId === nodeId
            const node = this.treeNodes.get(nodeId)

            updateTextSprite(label, nodeId, {
                fontSize: 24,
                textColor: isActive ? "#000000" : "#ffffff",
                fillStyle: "rgba(2, 6, 23, 0)",
                strokeStyle: "rgba(255, 255, 255, 0)",
                outlineColor: isActive
                    ? "rgba(255, 255, 255, 0.22)"
                    : "rgba(2, 6, 23, 0.92)",
                outlineWidth: isActive ? 4 : 8,
                shadowColor: "rgba(0, 0, 0, 0.35)",
                shadowBlur: 5,
            })

            const targetPosition = node.position.clone().add(new THREE.Vector3(0, 0, 0))
            label.position.lerp(targetPosition, 0.2)
        })
    }

    updateReturnBadges(elapsed) {
        this.returnBadges.forEach((badge, nodeId) => {
            const node = this.treeNodes.get(nodeId)
            const isActive = this.activeNodeId === nodeId
            const hasReturn = this.returnCounts.has(nodeId)
            const returnCount = hasReturn ? this.returnCounts.get(nodeId) : "-"
            const isCandidateLca = nodeId === this.candidateLcaId

            let fillStyle = "rgba(24, 24, 27, 0.96)"
            let textColor = "#cbd5e1"
            let strokeStyle = "rgba(255, 255, 255, 0.12)"
            let outlineColor = "rgba(0, 0, 0, 0.96)"

            if (hasReturn && returnCount === 0) {
                fillStyle = "rgba(30, 41, 59, 0.98)"
                textColor = "#e2e8f0"
                strokeStyle = "rgba(148, 163, 184, 0.32)"
            }

            if (hasReturn && returnCount === 1) {
                fillStyle = "rgba(13, 148, 136, 0.98)"
                textColor = "#f0fdfa"
                strokeStyle = "rgba(153, 246, 228, 0.34)"
            }

            if (hasReturn && returnCount === 2) {
                fillStyle = "rgba(107, 33, 168, 0.98)"
                textColor = "#faf5ff"
                strokeStyle = "rgba(216, 180, 254, 0.36)"
            }

            if (isCandidateLca) {
                fillStyle = "rgba(192, 38, 211, 0.98)"
                textColor = "#fdf4ff"
                strokeStyle = "rgba(244, 114, 182, 0.4)"
            }

            if (isActive) {
                fillStyle = "rgba(10, 10, 10, 0.98)"
                textColor = "#ffffff"
                strokeStyle = "rgba(255, 255, 255, 0.2)"
                outlineColor = "rgba(0, 0, 0, 0.98)"
            }

            updateTextSprite(badge, returnCount, {
                fontSize: 28,
                textColor,
                fillStyle,
                strokeStyle,
                outlineColor,
                outlineWidth: isActive ? 10 : 8,
                shadowColor: "rgba(0, 0, 0, 0.42)",
                shadowBlur: 6,
            })

            const pulse = isActive ? 0.08 + Math.sin(elapsed * 5.2) * 0.03 : 0
            const targetPosition = node.position.clone().add(new THREE.Vector3(0, 0.82 + pulse, 0))
            badge.position.lerp(targetPosition, 0.18)
        })
    }

    updateTargetBadges() {
        this.targetBadges.forEach((badge, nodeId) => {
            const node = this.treeNodes.get(nodeId)
            const targetPosition = node.position.clone().add(
                new THREE.Vector3(
                    nodeId === this.targetAId ? -0.34 : 0.34,
                    0.3,
                    1.5
                )
            )
            badge.position.lerp(targetPosition, 0.2)
        })
    }

    updateActiveRing(elapsed) {
        if (this.activeNodeId === null || !this.treeNodes.has(this.activeNodeId)) {
            this.activeRing.material.opacity = THREE.MathUtils.lerp(
                this.activeRing.material.opacity,
                0,
                0.18
            )
            return
        }

        const activeNode = this.treeNodes.get(this.activeNodeId)
        this.activeRing.position.lerp(
            activeNode.position.clone().add(new THREE.Vector3(0, 0, 0)),
            0.18
        )
        this.activeRing.scale.setScalar(1 + Math.sin(elapsed * 4.5) * 0.08)
        this.activeRing.material.opacity = THREE.MathUtils.lerp(
            this.activeRing.material.opacity,
            0.9,
            0.18
        )
    }

    onDispose() {
        const disposeSpriteTexture = (sprite) => {
            if (!sprite || !sprite.material || !sprite.material.map) {
                return
            }

            sprite.material.map.dispose()
        }

        this.nodeLabels.forEach(disposeSpriteTexture)
        this.returnBadges.forEach(disposeSpriteTexture)
        this.targetBadges.forEach(disposeSpriteTexture)
    }
}